/**
 * 환경별 로깅 유틸리티
 * development: console 로깅만 수행
 * production: Loki 로깅만 수행
 */
import lokiLogger, { userLokiLogger } from './loki';

interface ExtendedLoggerConfig extends LoggerConfig {
  includeUserInfo?: boolean;
}

class Logger {
  private config: ExtendedLoggerConfig;
  private isProduction: boolean;
  private lokiInstance: typeof lokiLogger;

  constructor(config: Partial<ExtendedLoggerConfig> = {}) {
    this.isProduction = process.env.NODE_ENV === 'production';
    this.config = {
      enabledLevels: ['log', 'info', 'warn', 'error', 'debug'],
      prefix: '',
      timestamp: !this.isProduction,
      includeUserInfo: false,
      ...config,
    };

    // includeUserInfo 옵션에 따라 적절한 Loki 인스턴스 선택
    this.lokiInstance = this.config.includeUserInfo ? userLokiLogger : lokiLogger;
  }

  private formatMessage(level: LogLevel, message: string): string {
    const parts: string[] = [];

    if (this.config.timestamp) {
      parts.push(`[${new Date().toISOString()}]`);
    }

    if (this.config.prefix) {
      parts.push(`[${this.config.prefix}]`);
    }

    parts.push(`[${level.toUpperCase()}]`);
    parts.push(message);

    return parts.join(' ');
  }

  /**
   * args에서 메타데이터 추출
   */
  private extractMetadata(args: any[]): Record<string, any> | undefined {
    if (args.length === 0) {
      return undefined;
    }

    // 첫 번째 인자가 객체면 메타데이터로 사용
    const firstArg = args[0];
    if (typeof firstArg === 'object' && firstArg !== null && !Array.isArray(firstArg)) {
      return firstArg;
    }

    return undefined;
  }

  log(message: string, ...args: any[]): void {
    if (this.isProduction) return;
    console.log(this.formatMessage('log', message), ...args);
  }

  info(message: string, ...args: any[]): void {
    if (this.isProduction)
      return this.lokiInstance.info(message, this.extractMetadata(args), this.config.prefix);
    console.info(this.formatMessage('info', message), ...args);
  }

  warn(message: string, ...args: any[]): void {
    if (this.isProduction)
      return this.lokiInstance.warn(message, this.extractMetadata(args), this.config.prefix);
    console.warn(this.formatMessage('warn', message), ...args);
  }

  error(message: string, ...args: any[]): void {
    if (this.isProduction)
      return this.lokiInstance.error(message, this.extractMetadata(args), this.config.prefix);
    console.error(this.formatMessage('error', message), ...args);
  }

  debug(message: string, ...args: any[]): void {
    if (this.isProduction) return;
    console.debug(this.formatMessage('debug', message), ...args);
  }

  /**
   * 그룹 로깅 (개발 환경에서만)
   */
  group(label: string): void {
    if (this.isProduction) return;
    console.group(label);
  }

  groupCollapsed(label: string): void {
    if (this.isProduction) return;
    console.groupCollapsed(label);
  }

  groupEnd(): void {
    if (this.isProduction) return;
    console.groupEnd();
  }

  /**
   * 테이블 형식 로깅 (개발 환경에서만)
   */
  table(data: any): void {
    if (this.isProduction) return;
    console.table(data);
  }

  /**
   * 성능 측정 (개발 환경에서만)
   */
  time(label: string): void {
    if (this.isProduction) return;
    console.time(label);
  }

  timeEnd(label: string): void {
    if (this.isProduction) return;
    console.timeEnd(label);
  }
}

/**
 * 특정 모듈용 로거 생성
 */
export const createLogger = (prefix: string, config?: Partial<ExtendedLoggerConfig>): Logger => {
  return new Logger({
    ...config,
    prefix,
  });
};

/**
 * 특정 모듈용 사용자 로거 생성 (merchantId, userId, phoneNum 자동 포함)
 */
export const createUserLogger = (
  prefix: string,
  config?: Partial<ExtendedLoggerConfig>,
): Logger => {
  return new Logger({
    ...config,
    prefix,
    includeUserInfo: true,
  });
};
